import { FormEvent, useEffect, useState } from 'react';
import { backendApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import SiteRangeField from '../components/SiteRangeField';
import {
  draftFromRange, FALLBACK_SCOPE, formatSiteRange, parseSiteRange, SiteScope,
} from '../lib/siteRange';

/** Error banner that a user can dismiss (×) and that auto-hides after 10s. */
function DismissibleError({ message, onClose }: { message: string; onClose: () => void }) {
  useEffect(() => {
    if (!message) return;
    const id = setTimeout(onClose, 10000);
    return () => clearTimeout(id);
  }, [message, onClose]);
  if (!message) return null;
  return (
    <div className="alert alert-error alert-dismiss">
      <span>{message}</span>
      <button className="alert-close" aria-label="Dismiss" onClick={onClose}>×</button>
    </div>
  );
}

/** Mirrors publicCoupon() in wpcursor-backend/src/routes/coupons.js */
interface Coupon {
  id: string;
  code: string;
  discountType: 'percent' | 'amount';
  discountValue: number;
  duration: 'once' | 'repeating' | 'forever';
  durationInMonths: number | null;
  maxUses: number | null;
  expiresAt: string | null;
  newCustomersOnly: boolean;
  active: boolean;
  timesRedeemed: number;
  campaign: string | null;
  notes: string | null;
  /** Agency site range; null = open end, both null = any number of sites. */
  minSites: number | null;
  maxSites: number | null;
  createdAt: string;
}

interface Redemption {
  accountId: string; email: string | null; subscriptionId: string | null; redeemedAt: string;
}

const discountLabel = (c: Coupon) =>
  c.discountType === 'percent'
    ? `${c.discountValue}% off`
    : `$${Number(c.discountValue).toFixed(2).replace(/\.00$/, '')} off`;

const durationLabel = (c: Coupon) =>
  c.duration === 'forever' ? 'Forever'
    : c.duration === 'repeating'
      ? `${c.durationInMonths ?? '?'} month${c.durationInMonths === 1 ? '' : 's'}`
      : 'First bill';

export default function Coupons() {
  const { can } = useAuth();
  const canWrite = can('coupons', 'write');

  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [redemptionsFor, setRedemptionsFor] = useState<Coupon | null>(null);
  const [editingSites, setEditingSites] = useState<Coupon | null>(null);

  // Slider bounds for the Sites control. Backend is the source of truth; fall back
  // to the Agency slider's 1–100 so the form still works if the call fails.
  const [scope, setScope] = useState<SiteScope>(FALLBACK_SCOPE);
  useEffect(() => {
    if (!canWrite) return;
    backendApi.get<SiteScope>('/license/admin/coupons/scope')
      .then((d) => {
        if (Number.isInteger(d?.minSites) && Number.isInteger(d?.maxSites) && d.minSites <= d.maxSites) {
          setScope({ minSites: d.minSites, maxSites: d.maxSites });
        }
      })
      .catch(() => { /* non-fatal: keep the 1–100 fallback */ });
  }, [canWrite]);

  async function load() {
    setLoading(true);
    try {
      const data = await backendApi.get<{ coupons: Coupon[] }>('/license/admin/coupons');
      setCoupons(data.coupons);
      setError('');
    } catch (err: any) {
      setError((err as ApiError).isForbidden ? 'You do not have access to coupons.' : err.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  async function toggle(c: Coupon) {
    setError('');
    try {
      await backendApi.patch(`/license/admin/coupons/${c.id}/active`, { active: !c.active });
      await load();
    } catch (err: any) {
      setError(err.body?.detail || err.message);
    }
  }

  async function remove(c: Coupon) {
    if (!confirm(`Delete coupon ${c.code}? Existing subscribers keep their discount.`)) return;
    setError('');
    try {
      await backendApi.delete(`/license/admin/coupons/${c.id}`);
      await load();
    } catch (err: any) { setError(err.message); }
  }

  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState('');
  async function syncFromStripe() {
    setSyncing(true); setSyncNote(''); setError('');
    try {
      const r = await backendApi.post<{ imported: number; scanned: number; skipped: number }>(
        '/license/admin/coupons/sync',
      );
      setSyncNote(
        r.imported > 0
          ? `Imported ${r.imported} coupon${r.imported === 1 ? '' : 's'} from Stripe (${r.skipped} deleted, skipped).`
          : `Already in sync — scanned ${r.scanned}, nothing new to import.`,
      );
      await load();
    } catch (err: any) {
      setError(err.body?.detail || err.message);
    } finally { setSyncing(false); }
  }

  return (
    <>
      <header className="page-head">
        <h1>Coupons</h1>
        <p className="muted">Discount codes, synced with Stripe.</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => void load()} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          {canWrite && (
            <>
              <button className="btn" onClick={syncFromStripe} disabled={syncing}>
                {syncing ? 'Syncing…' : 'Sync from Stripe'}
              </button>
              <button className="btn btn-primary" onClick={() => setCreating((v) => !v)}>
                {creating ? 'Cancel' : 'New coupon'}
              </button>
            </>
          )}
        </div>
      </header>

      {syncNote && <div className="alert" style={{ background: 'var(--color-status-success-bg)', color: 'var(--color-status-success-accent)' }}>{syncNote}</div>}

      <DismissibleError message={error} onClose={() => setError('')} />
      {creating && <CreateCoupon scope={scope} onDone={() => { setCreating(false); void load(); }} />}

      <div className="card">
        {loading ? (
          <p className="muted">Loading coupons…</p>
        ) : coupons.length === 0 ? (
          <p className="muted">No coupons yet.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Code</th><th>Discount</th><th>Duration</th><th>Sites</th><th>Uses</th>
                <th>Expires</th><th>Status</th><th />
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => (
                <tr key={c.id} className={c.active ? '' : 'row-muted'}>
                  <td>
                    <span className="mono">{c.code}</span>
                    {c.newCustomersOnly && <span className="tag">new only</span>}
                    {c.campaign && <span className="tag">{c.campaign}</span>}
                  </td>
                  <td><strong>{discountLabel(c)}</strong></td>
                  <td>{durationLabel(c)}</td>
                  <td data-testid={`sites-${c.code}`}>
                    <span className="mono">{formatSiteRange(c.minSites, c.maxSites)}</span>
                    {canWrite && (
                      <>
                        {' '}
                        <button className="btn btn-sm btn-ghost" aria-label={`Edit sites for ${c.code}`}
                          onClick={() => setEditingSites(c)}>Edit</button>
                      </>
                    )}
                  </td>
                  <td className="mono">
                    {c.timesRedeemed}{c.maxUses ? ` / ${c.maxUses}` : ''}
                  </td>
                  <td className="muted">
                    {c.expiresAt ? new Date(c.expiresAt).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    <span className={'pill ' + (c.active ? 'pill-ok' : 'pill-off')}>
                      {c.active ? 'active' : 'disabled'}
                    </span>
                  </td>
                  <td className="right">
                    <button className="btn btn-sm" onClick={() => setRedemptionsFor(c)}>
                      {c.timesRedeemed} redeemed
                    </button>
                    {canWrite && (
                      <>
                        {' '}
                        <button className="btn btn-sm" onClick={() => toggle(c)}>
                          {c.active ? 'Disable' : 'Enable'}
                        </button>
                        {' '}
                        <button className="btn btn-sm btn-danger" onClick={() => remove(c)}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editingSites && (
        <EditSites coupon={editingSites} scope={scope}
          onClose={() => setEditingSites(null)}
          onSaved={() => { setEditingSites(null); void load(); }} />
      )}

      {redemptionsFor && (
        <Redemptions coupon={redemptionsFor} onClose={() => setRedemptionsFor(null)} />
      )}
    </>
  );
}

function CreateCoupon({ scope, onDone }: { scope: SiteScope; onDone: () => void }) {
  const [f, setF] = useState({
    code: '', discountType: 'percent' as 'percent' | 'amount', discountValue: '',
    duration: 'once' as 'once' | 'repeating' | 'forever', durationInMonths: '',
    maxUses: '', expiresAt: '', newCustomersOnly: false, campaign: '', notes: '',
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [sites, setSites] = useState(() => draftFromRange({ minSites: null, maxSites: null }, scope));
  // Bounds can arrive after the form opens; re-seed only while the range is untouched ("Any").
  useEffect(() => {
    setSites((d) => (d.mode === 'any' ? draftFromRange({ minSites: null, maxSites: null }, scope) : d));
  }, [scope]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const range = parseSiteRange(sites, scope);
    if (!range.ok) { setError(range.error); return; }
    setError(''); setBusy(true);
    try {
      await backendApi.post('/license/admin/coupons', {
        code: f.code.trim() || undefined,
        discountType: f.discountType,
        discountValue: Number(f.discountValue),
        duration: f.duration,
        durationInMonths: f.duration === 'repeating' ? Number(f.durationInMonths) : undefined,
        maxUses: f.maxUses ? Number(f.maxUses) : undefined,
        expiresAt: f.expiresAt || undefined,
        newCustomersOnly: f.newCustomersOnly,
        campaign: f.campaign.trim() || undefined,
        notes: f.notes.trim() || undefined,
        minSites: range.minSites,
        maxSites: range.maxSites,
      });
      onDone();
    } catch (err: any) {
      setError(err.body?.detail || err.body?.error || err.message);
    } finally { setBusy(false); }
  }

  return (
    <form className="card" onSubmit={submit}>
      <h2 className="card-title">New coupon</h2>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="grid-2">
        <label className="field">
          <span>Code (blank = Stripe generates)</span>
          <input value={f.code} placeholder="SUMMER20"
            onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} />
        </label>
        <label className="field">
          <span>Discount type</span>
          <select value={f.discountType}
            onChange={(e) => setF({ ...f, discountType: e.target.value as any })}>
            <option value="percent">Percent (%)</option>
            <option value="amount">Fixed amount ($)</option>
          </select>
        </label>
        <label className="field">
          <span>Value {f.discountType === 'percent' ? '(e.g. 20 = 20%)' : '(dollars)'}</span>
          <input required type="number" min="1" step="0.01" value={f.discountValue}
            onChange={(e) => setF({ ...f, discountValue: e.target.value })} />
        </label>
        <label className="field">
          <span>Duration</span>
          <select value={f.duration}
            onChange={(e) => setF({ ...f, duration: e.target.value as any })}>
            <option value="once">Once</option>
            <option value="repeating">Repeating (months)</option>
            <option value="forever">Forever</option>
          </select>
        </label>
        {f.duration === 'repeating' && (
          <label className="field">
            <span>Months</span>
            <input required type="number" min="1" value={f.durationInMonths}
              onChange={(e) => setF({ ...f, durationInMonths: e.target.value })} />
          </label>
        )}
        <label className="field">
          <span>Max uses (blank = unlimited)</span>
          <input type="number" min="1" value={f.maxUses}
            onChange={(e) => setF({ ...f, maxUses: e.target.value })} />
        </label>
        <label className="field">
          <span>Expires (blank = never)</span>
          <input type="date" value={f.expiresAt}
            onChange={(e) => setF({ ...f, expiresAt: e.target.value })} />
        </label>
        <label className="field">
          <span>Campaign (optional)</span>
          <input value={f.campaign} onChange={(e) => setF({ ...f, campaign: e.target.value })} />
        </label>
      </div>
      <label className="field">
        <span>Notes (optional)</span>
        <input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </label>
      <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={f.newCustomersOnly}
          onChange={(e) => setF({ ...f, newCustomersOnly: e.target.checked })} />
        <span>New customers only</span>
      </label>

      <SiteRangeField value={sites} onChange={setSites} scope={scope} />

      <button className="btn btn-primary" disabled={busy}>{busy ? 'Creating…' : 'Create coupon'}</button>
    </form>
  );
}

/** Change an existing coupon's site range. Discount terms are immutable in Stripe; the range is ours. */
function EditSites({ coupon, scope, onClose, onSaved }: {
  coupon: Coupon; scope: SiteScope; onClose: () => void; onSaved: () => void;
}) {
  const [sites, setSites] = useState(() =>
    draftFromRange({ minSites: coupon.minSites, maxSites: coupon.maxSites }, scope));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function save(e: FormEvent) {
    e.preventDefault();
    const range = parseSiteRange(sites, scope);
    if (!range.ok) { setError(range.error); return; }
    setError(''); setBusy(true);
    try {
      await backendApi.patch(`/license/admin/coupons/${coupon.id}/scope`, {
        minSites: range.minSites, maxSites: range.maxSites,
      });
      onSaved();
    } catch (err: any) {
      setError(err.body?.detail || err.body?.error || err.message);
      setBusy(false);
    }
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <form className="modal" role="dialog" aria-label={`Sites for ${coupon.code}`}
        onClick={(e) => e.stopPropagation()} onSubmit={save}>
        <div className="modal-head">
          <h2 className="card-title" style={{ margin: 0 }}>
            Sites · <span className="mono">{coupon.code}</span>
          </h2>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>Close</button>
        </div>
        <p className="muted small" style={{ marginTop: 0 }}>
          Saved range: <strong>{formatSiteRange(coupon.minSites, coupon.maxSites)}</strong>.
          {' '}Changes apply to new redemptions; existing subscribers keep their discount.
        </p>
        {error && <div className="alert alert-error">{error}</div>}
        <SiteRangeField value={sites} onChange={setSites} scope={scope} />
        <button className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save sites'}</button>
      </form>
    </div>
  );
}

function Redemptions({ coupon, onClose }: { coupon: Coupon; onClose: () => void }) {
  const [rows, setRows] = useState<Redemption[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    backendApi
      .get<{ redemptions: Redemption[] }>(`/license/admin/coupons/${coupon.id}/redemptions`)
      .then((d) => setRows(d.redemptions))
      .catch((err) => setError(err.message));
  }, [coupon.id]);

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2 className="card-title" style={{ margin: 0 }}>
            Redemptions · <span className="mono">{coupon.code}</span>
          </h2>
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Close</button>
        </div>
        {error && <div className="alert alert-error">{error}</div>}
        {!rows ? (
          <p className="muted">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="muted">Not redeemed yet.</p>
        ) : (
          <table className="table">
            <thead><tr><th>Customer</th><th>Subscription</th><th>Redeemed</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    <div>{r.email || 'Unknown'}</div>
                    <div className="muted small mono">{r.accountId}</div>
                  </td>
                  <td className="mono muted small">{r.subscriptionId || '—'}</td>
                  <td className="muted">{new Date(r.redeemedAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
